import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ItemsService from '../services/items.service';
import { displayAlert } from './feedbackSlice';
import { manageError, removeObjectWithId, replaceObjectWithId } from '../utils/helpers';

// Categories
export const getCategories = createAsyncThunk(
  'items/categories/list',
  async (thunkAPI) => {
    try {
      const data = await ItemsService.getCategories();
      return { categories: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const createCategory = createAsyncThunk(
  'items/categories/post',
  async (categoryData, thunkAPI) => {
    try {
      const data = await ItemsService.createCategory(categoryData);
      return { category: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const updateCategory = createAsyncThunk(
  'items/categories/put',
  async (categoryData, thunkAPI) => {
    try {
      const data = await ItemsService.updateCategory(categoryData);
      return { category: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const deleteCategory = createAsyncThunk(
  'items/categories/delete',
  async (category, thunkAPI) => {
    try {
      await ItemsService.deleteCategory(category.id);
      return { category };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

// Lendings
export const getLendings = createAsyncThunk(
  'items/lendings/list',
  async (thunkAPI) => {
    try {
      const data = await ItemsService.getLendings();
      return { lendings: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const createLending = createAsyncThunk(
  'items/lendings/post',
  async (lendingData, thunkAPI) => {
    try {
      const data = await ItemsService.createLending(lendingData);
      return { lending: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const returnLending = createAsyncThunk(
  'items/lendings/return',
  async (lendingId, thunkAPI) => {
    try {
      const data = await ItemsService.returnLending(lendingId);
      return { lending: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

// Books
export const getBooks = createAsyncThunk(
  'items/books/list',
  async (
    filters,
    thunkAPI) => {
    try {
      const data = await ItemsService.getBooks(filters);
      return { books: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const createBook = createAsyncThunk(
  'items/books/post',
  async (bookData, thunkAPI) => {
    try {
      const data = await ItemsService.createBook(bookData);
      return { book: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const deleteBook = createAsyncThunk(
  'items/books/delete',
  async (book, thunkAPI) => {
    try {
      await ItemsService.deleteBook(book.id);
      return { book };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const exportBooks = createAsyncThunk(
  'items/books/export',
  async (_, thunkAPI) => {
    try {
      const response = await ItemsService.exportBooks();
      // Le nom vient de l'API, qui date le fichier. Le repli existe parce
      // que `Content-Disposition` n'est pas toujours lisible depuis le
      // navigateur : il faut que `Access-Control-Expose-Headers` l'autorise,
      // et un fichier sans nom serait enregistré en « download ».
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      return { blob: response.data, filename: match ? match[1] : 'collection.xlsx' };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const updateBook = createAsyncThunk(
  'items/books/update',
  async ({ bookData, bookId }, thunkAPI) => {
    try {
      const data = await ItemsService.updateBook(bookId, bookData);
      return { book: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

// Collections
export const getSharedCollection = createAsyncThunk(
  'items/collection/shared',
  async ({ slug, filters }, thunkAPI) => {
    try {
      const data = await ItemsService.getSharedCollection(slug, filters);
      return { sharedCollection: data.data };
    } catch (error) {
      // Un 404 n'est pas une panne : le lien est faux, ou la collection n'est
      // pas publique (l'API ne distingue pas les deux, exprès). La vitrine
      // l'affiche elle-même ; une alerte « erreur inconnue » par-dessus
      // laisserait croire à un incident.
      const status = error.response && error.response.status;
      if (status !== 404) {
        thunkAPI.dispatch(displayAlert(manageError(error), 'error'));
      }
      return thunkAPI.rejectWithValue({ status });
    }
  },
);

export const getSharedCategories = createAsyncThunk(
  'items/categories/shared',
  async ({ slug }, thunkAPI) => {
    try {
      const data = await ItemsService.getSharedCategories(slug);
      return { categories: data.data };
    } catch (error) {
      // Même 404 que la vitrine, pour la même raison : une seule explication
      // à l'écran, celle de la vitrine.
      if (!error.response || error.response.status !== 404) {
        thunkAPI.dispatch(displayAlert(manageError(error), 'error'));
      }
      return thunkAPI.rejectWithValue();
    }
  },
);

export const updateCollection = createAsyncThunk(
  'items/collections/update',
  async ({ id, ...champs }, thunkAPI) => {
    try {
      const data = await ItemsService.updateCollection(id, champs);
      return { collection: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue({ message });
    }
  },
);

export const getCollections = createAsyncThunk(
  'items/collections/list',
  async (thunkAPI) => {
    try {
      const data = await ItemsService.getCollections();
      return { collections: data.data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const itemsSlice = createSlice({
  name: 'items',
  initialState: {
    categories: [],
    lendings: [],
    books: {
      count: 0,
      num_pages: 0,
      results: [],
    },
    sharedCollection: {
      books: {
        count: 0,
        num_pages: 0,
        results: [],
      },
    },
    collections: [],
  },
  reducers: {},
  extraReducers: (builder) => {
    // Categories
    builder.addCase(getCategories.fulfilled, (state, action) => {
      state.categories = action.payload.categories;
    });
    builder.addCase(getCategories.rejected, (state, action) => {
      state.categories = [];
    });
    builder.addCase(createCategory.fulfilled, (state, action) => {
      state.categories = [...state.categories, action.payload.category];
    });
    builder.addCase(updateCategory.fulfilled, (state, action) => {
      const updatedCategory = action.payload.category;
      const oldCategories = [...state.categories];
      state.categories = replaceObjectWithId(oldCategories, updatedCategory);
    });
    builder.addCase(deleteCategory.fulfilled, (state, action) => {
      const oldCategory = action.payload.category;
      const oldCategories = [...state.categories];
      state.categories = removeObjectWithId(oldCategories, oldCategory);
    });

    // Lendings
    builder.addCase(getLendings.fulfilled, (state, action) => {
      state.lendings = action.payload.lendings;
    });
    builder.addCase(getLendings.rejected, (state, action) => {
      state.lendings = [];
    });
    builder.addCase(createLending.fulfilled, (state, action) => {
      state.lendings = [...state.lendings, action.payload.lending];
    });
    builder.addCase(returnLending.fulfilled, (state, action) => {
      const updatedLending = action.payload.lending;
      const oldLendings = [...state.lendings];
      state.lendings = removeObjectWithId(oldLendings, updatedLending);
    });
    builder.addCase(returnLending.rejected, (state, action) => {
      state.lendings = [...state.lendings];
    });

    // Books
    builder.addCase(getBooks.fulfilled, (state, action) => {
      state.books = action.payload.books;
    });
    builder.addCase(createBook.fulfilled, (state, action) => {
      state.books.results = [...state.books.results, action.payload.book];
    });
    builder.addCase(updateBook.fulfilled, (state, action) => {
      const updatedBook = action.payload.book;
      const oldBooks = [...state.books.results];
      state.books.results = replaceObjectWithId(oldBooks, updatedBook);
    });
    // ⛔ Il y avait ici un SECOND « returnLending.rejected », qui faisait
    // « state.books = [...state.books] ». Deux défauts en une ligne :
    //   1. `state.books` est un objet `{results: […]}`, pas un tableau — le
    //      décomposer LÈVE. Un retour de prêt en échec faisait donc planter
    //      l'écran, en production.
    //   2. Il portait la même clé que celui de la section « Prêts », vingt
    //      lignes plus haut. Dans un objet JavaScript, la dernière clé
    //      gagne : c'est cette ligne-ci qui s'exécutait, et l'autre jamais.
    // Rien ne le signalait. RTK 2 refuse deux réducteurs pour un même cas,
    // et c'est ainsi qu'on l'a vu.
    builder.addCase(deleteBook.fulfilled, (state, action) => {
      const oldBook = action.payload.book;
      const oldBooks = [...state.books.results];
      state.books.results = removeObjectWithId(oldBooks, oldBook);
    });

    // Shared Collection
    builder.addCase(getSharedCollection.pending, (state) => {
      state.sharedCollection.notFound = false;
    });
    builder.addCase(getSharedCollection.fulfilled, (state, action) => {
      state.sharedCollection = action.payload.sharedCollection;
    });
    builder.addCase(getSharedCollection.rejected, (state, action) => {
      // ⛔ Jamais `{}` : la vitrine lit `books.results` sans condition, et un
      // lien faux faisait planter la page entière (LA-BIBLI-FRONT-1, le
      // 20/10/2026, sur l'exemple VOTRE-IDENTIFIANT de la documentation).
      state.sharedCollection = {
        books: { count: 0, num_pages: 0, results: [] },
        notFound: !!action.payload && action.payload.status === 404,
      };
    });
    builder.addCase(getSharedCategories.fulfilled, (state, action) => {
      state.categories = action.payload.categories;
    });
    builder.addCase(getSharedCategories.rejected, (state, action) => {
      state.categories = [];
    });

    // Collections
    builder.addCase(updateCollection.fulfilled, (state, action) => {
      // On remplace la collection modifiée dans la liste : l'API fait foi.
      state.collections = state.collections.map(
        (collection) => (collection.id === action.payload.collection.id
          ? action.payload.collection
          : collection),
      );
    });
    builder.addCase(getCollections.fulfilled, (state, action) => {
      state.collections = action.payload.collections;
    });
    builder.addCase(getCollections.rejected, (state, action) => {
      state.collections = [];
    });
  },
});

export default itemsSlice.reducer;
